import React, { useEffect, useState, useRef } from "react";
import { Paragraph } from "../../Commons/Paragraph";
import { Colors } from "../../Commons/Colors/Colors";
import EyeIcon from "../../../assets/imgs/eye-white-icon.svg";
import { useNavigate } from "react-router-dom";
import { getPlayerStatusIcon } from "../PlayerScrollView/PlayerScrollView";

interface PlayerDetailsProps {
  name: string;
  position: string;
  posRank: string;
  ovrRank: string;
  status?: string;
  playerPos?: string;
}

const PlayerDetails: React.FC<PlayerDetailsProps> = ({
  name,
  position,
  posRank,
  ovrRank,
  status,
  playerPos
}) => {
  const [displayedPosRank, setDisplayedPosRank] = useState("--");
  const [displayedOvrRank, setDisplayedOvrRank] = useState("--");
  const [isLoading, setIsLoading] = useState(false);
  
  // Previous valid values
  const prevPosRank = useRef(posRank);
  const prevOvrRank = useRef(ovrRank);
-
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (posRank !== prevPosRank.current || ovrRank !== prevOvrRank.current) {
      setIsLoading(true);
      
      // Keep showing previous value during loading
      if (prevPosRank.current && prevPosRank.current !== "--") {
        setDisplayedPosRank(prevPosRank.current);
      }
      if (prevOvrRank.current && prevOvrRank.current !== "--") {
        setDisplayedOvrRank(prevOvrRank.current);
      }
      
      timeoutId = setTimeout(() => {
        // Update with new values only if they are valid
        if (posRank && posRank !== "--") {
          setDisplayedPosRank(posRank);
          prevPosRank.current = posRank;
        }
        if (ovrRank && ovrRank !== "--") {
          setDisplayedOvrRank(ovrRank);
          prevOvrRank.current = ovrRank;
        }
        setIsLoading(false);
      }, 150); // Slight delay for smooth transition
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [posRank, ovrRank]);

  const getPlayerStatusText = (status?: string) => {
    switch (status) {
      case "ACT":
        return "Active";
      case "INA":
        return "Injured";
      case "RES":
        return "Out";
      case "QUES":
        return "Ques";
      default:
        return "--";
    }
  };
  
  const navigate = useNavigate();

  return (
    <div className="flex-1">
      <Paragraph
        text={name.split(" ")[0]}
        className="text-2xl md:text-3xl lg:text-4xl !font-medium leading-[100%] hidden sm:block opacity-50"
        ParagraphType="Pragragh22"
        color="textPrimary"
      />
      <Paragraph
        text={name.split(" ")[1]}
        className="text-4xl sm:text-5xl md:text-6xl lg:text-[77.273px] !font-bold leading-[100%] hidden sm:block mb-6"
        ParagraphType="Pragragh22"
        color="textPrimary"
      />
      <Paragraph
        text={name}
        className={` ${name.length > 12 ? "text-2xl w-[90%]" : name.length > 20 ? "text-xl w-[90%]" : "text-3xl w-[80%]"} !font-bold !leading-[112%] sm:hidden`}
        ParagraphType="Pragragh22"
        color="textPrimary"
      />
      <Paragraph
        text={position}
        ParagraphType="Pragragh22"
        className="text-base sm:text-2xl md:text-3xl lg:text-[34.091px] !font-bold leading-[100%] mt-2"
        color="textPrimary"
      />
      <div className="flex items-center">
        <Paragraph
          text={getPlayerStatusText(status)}
          className="text-base sm:text-[18px] lg:text-[22px] !font-semibold leading-[100%] opacity-80 mt-3 sm:hidden mb-3"
          ParagraphType="Pragragh22"
          color="textPrimary"
        />
        {getPlayerStatusIcon(status)}
      </div>
      <Paragraph
        text={`Status:  ${status || "--"} `}
        className="text-base sm:text-[18px] lg:text-[22px] !font-semibold leading-[100%] opacity-80 mt-5 sm:block hidden"
        ParagraphType="Pragragh22"
        color="textPrimary"
      />
      
      <div
        style={{
          backdropFilter: "blur(4.5px)"
        }}
        className={`p-[10px] rounded-xl inline-flex gap-2 items-center mt-6 mb-12 sm:hidden bg-blur w-[198px]`}
        onClick={() => navigate("/rankings")}
      >
        <div className="text-center flex-1">
          <Paragraph
            text={displayedPosRank}
            className={`text-[20px] md:text-3xl lg:text-[34.091px] !font-bold leading-[100%] transition-all duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'}`}
            ParagraphType="Pragragh22"
            color="textPrimary"
          />
          <Paragraph
            text="2024 Rank"
            className="text-[10px] md:text-xs lg:text-sm opacity-70 whitespace-nowrap"
            ParagraphType="Pragragh22"
            color="textPrimary"
          />
        </div>
        <div
          className="h-[35.5px] w-[1px]"
          style={{ backgroundColor: Colors.backgroundSecundary }}
        ></div>
        <div className="text-center flex-1 ">
          <Paragraph
            text={displayedOvrRank}
            className={`text-[20px] md:text-3xl lg:text-[34.091px] !font-bold leading-[100%] transition-all duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'}`}
            ParagraphType="Pragragh22"
            color="textPrimary"
          />
          <div className="flex items-center gap-1 justify-center">
            <img className="h-[12px]" src={EyeIcon} alt={"EyeIcon"} />
            <Paragraph
              text="Season Proj"
              className="text-[10px] md:text-xs lg:text-sm opacity-70 whitespace-nowrap"
              ParagraphType="Pragragh22"
              color="textPrimary"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetails;
